import { useState } from "react";
import { Link } from "react-router-dom";
import { Trash2, ShoppingBag } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";

export default function Cart() {
  const { items, removeItem, subtotal, total, clearCart } = useCart();
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const checkout = async () => {
    if (!items.length) return;
    if (!user && !email) { toast.error("Please enter email or sign in"); return; }
    setLoading(true);
    try {
      const payload = {
        items: items.map((i) => ({
          product_id: i.product_id,
          format: i.format,
          quantity: i.quantity || 1,
        })),
        origin_url: window.location.origin,
        email: user ? null : email,
      };
      const { data } = await api.post("/checkout/session", payload);
      localStorage.setItem("hoh_pending_order_id", data.order_id);
      if (!user) localStorage.setItem("hoh_guest_email", email);
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16" data-testid="cart-page">
      <div className="label-eyebrow mb-3">Your Basket</div>
      <h1 className="font-serif-display text-5xl text-white">Review & <span className="italic text-[#D4AF37]">checkout</span></h1>

      {items.length === 0 ? (
        <div className="mt-16 text-center">
          <ShoppingBag size={48} className="mx-auto text-neutral-700" />
          <p className="text-neutral-400 mt-6">Your basket is empty.</p>
          <Link to="/music" className="btn-gold mt-6 inline-block px-6 py-3 rounded text-xs tracking-widest uppercase">Browse Store</Link>
        </div>
      ) : (
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-4">
            {items.map((i) => (
              <div key={i.key} data-testid={`cart-item-${i.key}`} className="luxury-card flex items-center gap-4 p-4 rounded-xl">
                <img src={i.cover_image_url} alt={i.title} className="w-20 h-20 rounded object-cover" />
                <div className="flex-1 min-w-0">
                  <div className="font-serif-display text-lg text-white truncate">{i.title}</div>
                  <div className="text-xs tracking-widest uppercase text-neutral-500 mt-1">
                    {i.format} · {i.type}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-serif-display text-xl text-[#D4AF37]">£{(Number(i.price) * (i.quantity || 1)).toFixed(2)}</div>
                </div>
                <button data-testid={`remove-${i.key}`} onClick={() => removeItem(i.key)} className="text-neutral-400 hover:text-red-400 p-2">
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>

          <div className="luxury-card rounded-xl p-6 h-fit">
            <div className="label-eyebrow mb-3">Order Summary</div>
            <div className="flex justify-between py-2 text-sm">
              <span className="text-neutral-400">Subtotal</span>
              <span className="text-white">£{subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between py-3 border-t border-white/10 mt-2">
              <span className="text-neutral-400">Total</span>
              <span data-testid="cart-total" className="font-serif-display text-2xl text-[#D4AF37]">£{total.toFixed(2)}</span>
            </div>
            <p className="text-[10px] text-neutral-500 mt-2 tracking-widest uppercase">Digital delivery · No shipping required</p>
            {!user && (
              <div className="mt-4">
                <label className="text-xs tracking-widest uppercase text-neutral-400">Guest Email</label>
                <input
                  data-testid="guest-email"
                  type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@email.com"
                  className="mt-2 w-full bg-black border border-white/15 focus:border-[#D4AF37] outline-none px-3 py-2 rounded text-sm text-white"
                />
                <Link to="/login" className="text-xs text-[#D4AF37] hover:text-[#FCE68A] mt-2 inline-block">Or sign in for download history →</Link>
              </div>
            )}
            <button
              data-testid="checkout-btn"
              onClick={checkout} disabled={loading}
              className="btn-gold w-full mt-6 py-3 rounded text-sm tracking-widest uppercase font-semibold">
              {loading ? "Redirecting..." : "Proceed to Checkout"}
            </button>
            <button data-testid="clear-cart-btn" onClick={clearCart} className="block mx-auto mt-4 text-xs text-neutral-500 hover:text-red-400">Clear basket</button>
          </div>
        </div>
      )}
    </div>
  );
}
